/**
 * The shell.
 *
 * It owns three things and delegates the rest: the credential lifecycle, the
 * §13 metrics poll, and the reading history the sparklines are drawn from.
 *
 * Metrics live here rather than in the guardrails screen for two reasons. The
 * nav badges need the breach count whichever screen you are on, and a series
 * that only accumulates while you happen to be looking at it is a useless
 * series. The poll is deliberately slow — every read writes an audit row (§07),
 * and audit noise has a cost.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError,
  apiBase,
  clearCredential,
  currentActor,
  getMetrics,
  setUnauthorizedHandler,
  signIn,
} from './api';
import type { MetricsResponse } from './api';
import { formatClock } from './views/kit';
import Gate from './views/Gate';
import Queue from './views/Queue';
import CaseDetail from './views/CaseDetail';
import Guardrails from './views/Guardrails';
import type { History } from './views/Guardrails';
import Waitlist from './views/Waitlist';
import Pricing from './views/Pricing';

const POLL_MS = 120_000;
const MAX_POINTS = 120;

type Route =
  | { view: 'queue' }
  | { view: 'case'; id: string }
  | { view: 'guardrails' }
  | { view: 'waitlist' }
  | { view: 'pricing' };

function parseHash(hash: string): Route {
  const path = hash.replace(/^#\/?/, '');
  if (path.startsWith('case/')) {
    const id = decodeURIComponent(path.slice('case/'.length));
    if (id.length > 0) return { view: 'case', id };
  }
  if (path === 'guardrails') return { view: 'guardrails' };
  if (path === 'waitlist') return { view: 'waitlist' };
  if (path === 'pricing') return { view: 'pricing' };
  return { view: 'queue' };
}

function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}

function navigate(hash: string): void {
  window.location.hash = hash;
}

/** Append a reading per guardrail, keeping the series bounded. */
function appendReadings(prev: History, metrics: MetricsResponse): History {
  const t = Date.parse(metrics.generatedAt) || Date.now();
  const next: History = { ...prev };
  for (const rail of metrics.guardrails) {
    if (rail.value === null) continue;
    const series = next[rail.key] ?? [];
    const last = series[series.length - 1];
    if (last && last.t === t) continue;
    next[rail.key] = [...series, { t, v: rail.value }].slice(-MAX_POINTS);
  }
  return next;
}

export default function App() {
  const [authed, setAuthed] = useState(false);
  const [gateBusy, setGateBusy] = useState(false);
  const [gateError, setGateError] = useState<ApiError | null>(null);

  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<ApiError | null>(null);
  const [history, setHistory] = useState<History>({});
  const [overdue, setOverdue] = useState<number | null>(null);
  // Plans priced away from their §12 default. Reported by the pricing screen
  // once it has read, like the queue's overdue count — nothing is guessed.
  const [priceOverrides, setPriceOverrides] = useState<number | null>(null);

  const route = useRoute();
  const inFlight = useRef(false);

  const lock = useCallback(() => {
    clearCredential();
    setAuthed(false);
    setMetrics(null);
    setMetricsError(null);
    setHistory({});
    setOverdue(null);
    setPriceOverrides(null);
  }, []);

  // A key rejected mid-session drops the whole console, not just one screen.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAuthed(false);
      setMetrics(null);
      setHistory({});
      setOverdue(null);
      setPriceOverrides(null);
      setGateError(new ApiError(401, 'expired', 'That key stopped being accepted. Sign in again.'));
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const refreshMetrics = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setMetricsLoading(true);
    try {
      const res = await getMetrics();
      setMetrics(res);
      setMetricsError(null);
      setHistory((prev) => appendReadings(prev, res));
    } catch (err) {
      setMetricsError(err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err)));
    } finally {
      inFlight.current = false;
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    const id = window.setInterval(() => {
      void refreshMetrics();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [authed, refreshMetrics]);

  async function handleSignIn(key: string, actor: string) {
    setGateBusy(true);
    setGateError(null);
    try {
      const res = await signIn(key, actor);
      setMetrics(res);
      setHistory(appendReadings({}, res));
      setMetricsError(null);
      setAuthed(true);
    } catch (err) {
      setGateError(err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err)));
    } finally {
      setGateBusy(false);
    }
  }

  if (!authed) {
    return <Gate onSubmit={(k, a) => void handleSignIn(k, a)} busy={gateBusy} error={gateError} />;
  }

  const breached = metrics?.breached.length ?? null;
  const pausedClusters = metrics?.ratios.filter((r) => r.paused).length ?? null;
  const actor = currentActor();

  const title =
    route.view === 'queue'
      ? 'Queue'
      : route.view === 'case'
        ? 'Case'
        : route.view === 'guardrails'
          ? 'Guardrails'
          : route.view === 'waitlist'
            ? 'Waitlist'
            : 'Pricing';

  return (
    <div className="shell">
      <nav className="rail" aria-label="Console sections">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            আ
          </span>
          <span>
            <span className="brand-name">Alap</span>
            <br />
            <span className="brand-sub">moderation</span>
          </span>
        </div>

        <div className="nav">
          <NavItem
            label="Queue"
            active={route.view === 'queue' || route.view === 'case'}
            onClick={() => navigate('#/queue')}
            badge={overdue}
            tone={overdue !== null && overdue > 0 ? 'alert' : 'calm'}
            badgeTitle="cases past their SLA"
          />
          <NavItem
            label="Guardrails"
            active={route.view === 'guardrails'}
            onClick={() => navigate('#/guardrails')}
            badge={breached}
            tone={breached !== null && breached > 0 ? 'alert' : 'calm'}
            badgeTitle="guardrails over their threshold"
          />
          <NavItem
            label="Waitlist"
            active={route.view === 'waitlist'}
            onClick={() => navigate('#/waitlist')}
            badge={pausedClusters}
            tone={pausedClusters !== null && pausedClusters > 0 ? 'alert' : 'calm'}
            badgeTitle="clusters with male intake paused"
          />
          <NavItem
            label="Pricing"
            active={route.view === 'pricing'}
            onClick={() => navigate('#/pricing')}
            badge={priceOverrides}
            tone="neutral"
            badgeTitle="plans priced away from their §12 default"
          />
        </div>

        <div className="rail-foot">
          <div>
            <span className="eyebrow">Signed in as</span>
            <br />
            {actor ?? 'unnamed — the audit log records the key fingerprint'}
          </div>
          <div>
            <span className="eyebrow">API</span>
            <br />
            <code>{apiBase}</code>
          </div>
          <button type="button" className="btn btn-sm" onClick={lock}>
            Lock console
          </button>
          <span style={{ fontSize: 11 }}>
            The key is in memory only. Closing the tab signs you out.
          </span>
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          <h1>{title}</h1>
          <span className="spacer" />
          <span className="topbar-meta">
            {metricsError ? (
              <span style={{ color: 'var(--alta)' }}>metrics stale</span>
            ) : metrics ? (
              <>
                guardrails read <span className="num">{formatClock(metrics.generatedAt)}</span>
              </>
            ) : (
              'no reading yet'
            )}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => void refreshMetrics()}
            disabled={metricsLoading}
          >
            {metricsLoading ? 'Reading…' : 'Refresh metrics'}
          </button>
        </header>

        <main className="content">
          {route.view === 'queue' && (
            <Queue onOpenCase={(id) => navigate(`#/case/${encodeURIComponent(id)}`)} onOverdueCount={setOverdue} />
          )}
          {route.view === 'case' && <CaseDetail caseId={route.id} onBack={() => navigate('#/queue')} />}
          {route.view === 'guardrails' && (
            <Guardrails
              metrics={metrics}
              loading={metricsLoading}
              error={metricsError}
              history={history}
              onRefresh={() => void refreshMetrics()}
            />
          )}
          {route.view === 'waitlist' && (
            <Waitlist
              metrics={metrics}
              loading={metricsLoading}
              error={metricsError}
              onRefresh={() => void refreshMetrics()}
            />
          )}
          {route.view === 'pricing' && <Pricing onOverriddenCount={setPriceOverrides} />}
        </main>
      </div>
    </div>
  );
}

function NavItem({
  label,
  active,
  onClick,
  badge,
  tone,
  badgeTitle,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  badge: number | null;
  /** 'neutral' is the unstyled badge — for a count that is a fact, not a state. */
  tone: 'alert' | 'calm' | 'neutral';
  badgeTitle: string;
}) {
  const toneClass = tone === 'alert' ? 'is-alert' : tone === 'calm' ? 'is-calm' : '';
  return (
    <button
      type="button"
      className="nav-item"
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
    >
      {label}
      {badge !== null && (
        <span className={`nav-badge ${toneClass}`} title={badgeTitle}>
          {badge}
        </span>
      )}
    </button>
  );
}
